import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useMemo } from 'react'
import { IMainState } from 'store/states'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { joinKeys } from 'helpers/translations.helper'
import { State } from 'components/ui/externalCourses'
import { useAccess } from 'contexts/AccessContext'
import { EnrolExternalCourse, Area } from 'graphqlAPI'
import { prefixTranslation } from '../../../../common'

export function useColumns() {
  const { t } = useTranslation()
  const { access } = useAccess()
  const canViewParticipantProfile = access.canViewParticipantProfile()
  const externalCourse = useSelector(
    ({ externalCourses }: IMainState) => externalCourses.externalCourse.data
  )
  type Column = {
    title: string
    key: string | 'action'
    sorteable?: boolean
    dataIndex?: string
    width?: number
    render?: (data: any, all: EnrolExternalCourse) => any
  }

  const prefix = joinKeys(prefixTranslation, 'statistics')
  const prefixTable = joinKeys(prefix, 'table')
  const prefixHeaders = joinKeys(prefixTable, 'headers')
  const prefixContent = joinKeys(prefixTable, 'content')

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefixHeaders}.name`),
        key: 'user_full_name',
        dataIndex: 'user_full_name',
        sorteable: true,
        width: 100,
        render: (_value, { user }: EnrolExternalCourse) => {
          const { id, avatar, name, lastname, position, abacRole } = user
          const fullName = [name, lastname].join(' ') || ''
          const avatarNode = (
            <UserAvatar
              user={{
                fullName,
                position: position || '',
                avatar: avatar || '',
                isDefaultAbacAdmin: !!abacRole?.isDefault,
              }}
              showAdmin
              classes={{ userName: 'text-truncate' }}
            />
          )

          if (!canViewParticipantProfile) {
            return avatarNode
          }

          return (
            <a href={`/configuration/user-profile/${id}`} target="_blank">
              {avatarNode}
            </a>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.position`),
        key: 'user.position',
        width: 170,
        sorteable: true,
      },
      {
        title: t(`${prefixHeaders}.area`),
        key: 'user.area',
        width: 170,
        sorteable: true,
        render: (area: Area) => area?.name,
      },
      {
        title: t(`${prefixHeaders}.state`),
        key: 'state',
        width: 170,
        sorteable: true,
        render: (state: number) => <State state={state} />,
      },
      {
        title: t(`${prefixHeaders}.attendance`),
        key: 'attendance',
        width: 80,
        sorteable: true,
        render: (attendance: string) => `${parseInt(attendance)}%`,
      },
      {
        title: t(`${prefixHeaders}.totalHours`),
        key: 'totalHours',
        width: 60,
        sorteable: true,
        render: (totalHours: string) =>
          t(`${prefixContent}.totalHours`, { count: +totalHours }),
      },
      ...new Array(externalCourse?.evaluationsCount || 0).fill(0).map(
        (_, index) =>
          ({
            title: t(`${prefixHeaders}.evaluation`, {
              index: index + 1,
            }),
            key: `synchronous_course_evaluations_${index}`,
            width: 120,
            sorteable: false,
            render: (_, { evaluations }) => {
              const evaluation = evaluations?.[index]
              return (
                <div className="text-center">{evaluation?.points || '-'}</div>
              )
            },
          } as Column)
      ),
    ],
    [
      canViewParticipantProfile,
      externalCourse?.evaluationsCount,
      prefixContent,
      prefixHeaders,
      t,
    ]
  )
  return columns
}
